import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components/macro';
import { Helmet } from 'react-helmet';
import debounce from 'lodash.debounce';
import queryString from 'query-string';

import { logToAmplitude } from 'helpers/logAmplitudeEvent';

import TopBar from 'components/Header/TopBar/TopBar';
import Header from 'components/Header/Header';
import InputForm from 'components/search/InputForm/InputForm';
import RadioGroup from 'components/base/RadioGroup/RadioGroup';
import Dropdown from 'components/base/Dropdown/Dropdown';
import Layout from 'components/styled/Layout/Layout';
import Content from 'components/styled/Content/Content';
import ResultsSection from 'components/search/ResultsSection/ResultsSection';
import RecentEntities from 'components/search/RecentEntities/RecentEntities';

import ScrollTopOnMount from 'components/utility/ScrollTopOnMount/ScrollTopOnMount';
import BackToTop from 'components/BackToTop/BackToTop';
import Footer from 'components/Footer/Footer';

import modularScale from 'styles/helpers/modular-scale';
import { showMoreButton } from 'styles/base-classes';

import getSearchURL from 'url-constructors/getSearchURL';
import { RootState } from 'redux/rootReducer';
import { useLocation, match, useHistory } from 'react-router';
import Modal from 'components/utility/Modal/Modal';
import AddEntityToListView from 'components/customlists/AddToList/AddEntityToListView';
import { EntityTypes } from 'context/entityContext';
import NotificationSettingsModal from 'components/notifications/Settings';
import { Label } from 'components/base/RadioGroup/RadioGroup';
import { CompilationSwitcher } from 'components/search/SearchDropdown/SearchDropdown.styles';
import Switcher from 'components/Switcher';
import Box from 'components/elements/Box';

const LayoutWrapper = styled('div')`
  background: white;
  min-height: 75vh;
  padding: ${({ theme }): string =>
    `${theme.spacing.small} ${theme.spacing.medium}`};

  @media screen and (min-width: 43em) {
    width: ${({ theme }): number => theme.layout.smallScreen * 100}%;
    padding: ${({ theme }): string =>
      `${theme.spacing.medium} ${theme.spacing.small}`};

    /** 
     * Make sure that children with negative horizontal margins of modularScale(0) 
     * never overflow the viewport 
    */
    max-width: calc(100% - (${modularScale(0)} * 2));
    margin: 0 auto;
    align-self: center;
  }

  @media screen and (min-width: 60em) {
    padding: ${({ theme }): string => theme.spacing.xlarge};
    width: ${({ theme }): number => theme.layout.largeScreen * 100}%;
  }
  @media screen and (min-width: 80em) {
    padding: ${({ theme }): string =>
      `${theme.spacing.xlarge} ${theme.spacing.huge}`};
  }
`;

const SearchTipParagraph = styled('p')`
  width: 100%;
  text-align: center;
  margin-top: ${({ theme }): string => theme.spacing.xlarge};
  color: ${({ theme }): string => theme.CasperGrey};
`;

const ButtonWrapper = styled('div')`
  margin: 0 -${({ theme }): string => theme.spacing.small};

  button {
    ${showMoreButton};
  }
`;

const RadioGroupWrapper = styled('div')`
  ${Label} {
    &:not(:last-of-type) {
      border-right: initial;
    }
  }
`;

interface ISearchMatchParams {
  searchTerm: string;
}

interface ISearchProps {
  isExternalOneLabel: boolean;
  match: match<ISearchMatchParams>;
}

export interface IEntityInfo {
  entityId: string;
  entityName: string;
  apiEntityType: EntityTypes;
}

const Search: React.FC<ISearchProps> = (props) => {
  const { isExternalOneLabel, match } = props;
  const mq = window.matchMedia('(min-width: 44em)');
  const entityTypes = [
    { label: 'All results', value: 'all' },
    { label: 'Tracks', value: 'track' },
    { label: 'Releases', value: 'release' },
    { label: 'Artists', value: 'artist' },
    { label: 'Projects', value: 'project' },
  ];
  if (!isExternalOneLabel) {
    entityTypes.push({ label: 'Labels', value: 'label' });
  }
  const [showRadioGroup, setShowRadioGroup] = useState(mq.matches);
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [entityType, setEntityType] = useState('all');
  const [searchTerm, setSearchTerm] = useState(match.params.searchTerm);
  const [entityInfo, setEntityInfo] = useState<IEntityInfo | undefined>();
  const [startSearchTime, setStartSearchTime] = useState<Date | undefined>();
  const [isAllAlbumSearch, setIsAllAlbumSearch] = useState<boolean>(true);

  const location = useLocation();
  const history = useHistory();

  const updateSearchValue = (value: string): void => setSearchValue(value);

  const debouncedUpdateSearchValue = useCallback(
    debounce(updateSearchValue, 500),
    []
  );

  useEffect(() => {
    mq.addListener(updateLayout);
    return (): void => {
      mq.removeListener(updateLayout);
    };
  }, [mq]);

  useEffect(() => {
    const searchParams = queryString.parse(location.search);
    const query = searchParams.query;

    const hasValidType = entityTypes.some(
      ({ value }) => value === match.params.searchTerm
    );

    if (hasValidType) {
      // When the component mounts the entity type defaults to 'all',
      // but if the URL contains a valid entity type, change it to that.
      // This happens for instance when clicked on an entity from the results
      // but the user went back and the entity route doesn't match the entity state.
      setEntityType(match.params.searchTerm);
    } else if (location.pathname !== '/search') {
      // When the URL doesn't contain a valid entity type (e.g '/search/track')
      // and it also isn't the default ('/search' a.k.a entityType='all'),
      // the URL is broken (e.g '/search/track') so we redirect back to the default.

      history.replace({
        ...location,
        pathname: '/search',
        search: queryString.stringify(searchParams),
      });
    }

    // Same story as with hasValidType but then for query.
    setInputValue((query as string) ?? '');
    debouncedUpdateSearchValue((query as string) ?? '');
  }, [
    entityTypes,
    history,
    location,
    debouncedUpdateSearchValue,
    match.params.searchTerm,
  ]);

  useEffect(() => {
    // Is the incoming route not the same as the current route?
    if (match.params.searchTerm !== searchTerm) {
      setSearchTerm(match.params.searchTerm);
      // Is the route exactly '/search'?
      if (location.pathname === '/search') {
        setEntityType('all');
        // If the entity type is switched the query should should be remembered.
        history.replace({
          ...location,
          pathname: '/search',
          search: location.search,
        });
      } else {
        // The route was changed to a different entity other than 'all' ('/search')
        setEntityType(match.params.searchTerm);
        // If the entity type is switched the query should should be remembered.
        history.replace({
          ...location,
          pathname: `/search/${match.params.searchTerm}`,
          search: location.search,
        });
      }
    }
  }, [match, location, history, entityType, searchTerm]);

  useEffect(() => {
    setStartSearchTime(new Date());
  }, [searchValue]);

  /**
   * This method gets passed a boolean from the mediaquery (window.matchMedia)
   * and sets the state whether to show the RadioGroup or the Dropdown
   */
  const updateLayout = ({ matches }: { matches: boolean }): void =>
    setShowRadioGroup(matches);

  /**
   * Callback for the Dropdown, RadioGroup, and 'Show more' button.
   * This function manages the entity type state and keeps it in sync with the URL.
   */
  const handleEntityChange = (value: string): void => {
    const searchParams = queryString.parse(location.search);
    setEntityType(value);

    // It is possible an input event fires multiple times so to make sure
    // the history isn't filled by duplicates we do these checks.
    if (value === 'all' && location.pathname === '/search') {
      return;
    }
    if (location.pathname === `/search/${value}`) {
      return;
    }

    history.push({
      ...location,
      pathname: value === 'all' ? '/search' : `/search/${value}`,
      search: queryString.stringify({
        ...searchParams,
        query: inputValue.length < 1 ? undefined : inputValue,
      }),
    });
  };

  /** Callback for submitting the search field */
  const handleSubmit = (event: any): void => {
    event.preventDefault();
    (document.activeElement as any)?.blur();
  };

  /** We want the search field to be at the top of the page when it's focused */
  const handleFocus = (evt: any): void => {
    if ('scrollIntoView' in evt.target && !mq.matches) {
      evt.target.scrollIntoView(true);
    }
  };

  /** Callback for the search input. Updates the input value in state and URL */
  const handleChange = ({ target: { value } }: { target: any }): void => {
    const searchParams = queryString.parse(location.search);

    history.push({
      ...location,
      search: queryString.stringify({
        ...searchParams,
        query: value.length < 1 ? undefined : value,
      }),
    });
  };

  const getSearchTime = (startTime: Date): number => {
    const endSearchTime = new Date().valueOf();
    return (endSearchTime - startTime.valueOf()) / 1000;
  };

  const onAddToListClick = (entityInfo: IEntityInfo): void => {
    setEntityInfo(entityInfo);

    if (searchValue) {
      const searchTime = getSearchTime(startSearchTime || new Date());
      logToAmplitude('Search', { searchValue, searchTime });
    }
  };

  const onOpenEntity = (): void => {
    const searchTime = getSearchTime(startSearchTime || new Date());
    logToAmplitude('Search', { searchValue, searchTime });
  };

  const hideModal = (): void => {
    setEntityInfo(undefined);
  };

  return (
    <>
      <Helmet defer={false} title="Search" />
      <ScrollTopOnMount />

      <NotificationSettingsModal />

      <Header>
        <TopBar entityTitle="Search" isStatic />
        <Layout.MaxWidth>
          <InputForm
            entityType={entityType}
            invalid={false}
            handleSubmit={handleSubmit}
            handleChange={handleChange}
            handleFocus={handleFocus}
            value={inputValue}
          />
        </Layout.MaxWidth>
      </Header>

      <Content>
        <LayoutWrapper>
          {showRadioGroup ? (
            <Layout.Center>
              <RadioGroupWrapper>
                <RadioGroup
                  options={entityTypes}
                  name="entity"
                  selectedOption={entityType}
                  onChange={({ value }: { value: string }): void =>
                    handleEntityChange(value)
                  }
                  isButtonNewDesigned
                />
              </RadioGroupWrapper>
            </Layout.Center>
          ) : (
            <Dropdown
              items={entityTypes}
              selectedItem={entityTypes.find((obj) => obj.value === entityType)}
              onChange={({ value }: { value: string }): void =>
                handleEntityChange(value)
              }
              isRoundedDropdown="all"
            />
          )}
          <>
            {entityType === 'release' && (
              <Box ml={3} mt={5} mb={-3}>
                <CompilationSwitcher alignSelf="center">
                  <Switcher
                    componentName="search-releases"
                    isChecked={isAllAlbumSearch}
                    rightLabel="View Compilations"
                    setCheck={setIsAllAlbumSearch}
                    isToggleSwitcher
                  />
                </CompilationSwitcher>
              </Box>
            )}
            {searchValue.length === 0 ? (
              <RecentEntities data={[]} onAddToListClick={onAddToListClick} />
            ) : entityType === 'all' ? (
              <>
                {entityTypes.map(({ label, value }) =>
                  value === 'all' ? null : (
                    <ResultsSection
                      key={value}
                      entityType={value}
                      title={label}
                      url={getSearchURL({
                        entityType: value as EntityTypes,
                        limit: 6,
                        query: searchValue,
                      })}
                      searchValue={searchValue}
                      displayLimit={5}
                      onAddToListClick={onAddToListClick}
                      onOpenEntity={onOpenEntity}
                    >
                      <ButtonWrapper>
                        <button onClick={(): void => handleEntityChange(value)}>
                          Show all {value} results
                        </button>
                      </ButtonWrapper>
                    </ResultsSection>
                  )
                )}
              </>
            ) : (
              <>
                <ResultsSection
                  title={`${entityType}s`}
                  url={getSearchURL({
                    entityType: entityType as EntityTypes,
                    query: searchValue,
                    releaseData:
                      entityType === 'release' && !isAllAlbumSearch
                        ? 'compilation'
                        : undefined,
                  })}
                  entityType={
                    entityType === 'release' && !isAllAlbumSearch
                      ? 'compilation'
                      : entityType
                  }
                  inputValue={inputValue}
                  searchValue={searchValue}
                  onAddToListClick={onAddToListClick}
                  onOpenEntity={onOpenEntity}
                />
                <SearchTipParagraph>
                  Didn&apos;t find what you&apos;re looking for? <br /> Try
                  again with a different search term or try a different
                  spelling.
                </SearchTipParagraph>
              </>
            )}
          </>
        </LayoutWrapper>
        {entityInfo && (
          <Modal hide={hideModal} canClickOutside={true}>
            <AddEntityToListView
              close={hideModal}
              entityId={entityInfo.entityId}
              entityName={entityInfo.entityName}
              entityType={entityInfo.apiEntityType}
            />
          </Modal>
        )}
      </Content>
      <BackToTop />
      <Footer />
    </>
  );
};

export default connect((state: RootState): any => ({
  isExternalOneLabel: state.authentication.isExternalOneLabel,
}))(Search);
